import { MoreVert } from '@mui/icons-material'
import { ColumnDef } from '@tanstack/react-table'
import { ChangeEvent } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { Switch } from 'components/Form'
import { LastModifiedCell } from 'components/Table'
import { COLUMN_SIZE } from 'components/Table/constants'
import { Alert } from 'features/alerts/types/alert'
import { AdUnitAlerts } from 'features/inventory/ad-unit/components/AdUnitAlerts'
import { KNOWLEDGE_BASE_URL } from 'features/inventory/ad-unit/constants'
import { usePriceGeniusAlwaysOn } from 'features/inventory/ad-unit/hooks/usePriceGeniusAlwaysOn'
import { AdUnitAlert } from 'features/inventory/ad-unit/types/adUnitAlert'
import { useAbility } from 'providers/casl'
import { AdUnitPath } from './hooks/useGetAdUnitPaths'
import { AdUnitsAttributesSchema } from './schema'
import { AdUnitForProductTable, AdUnitsTableProduct } from './types'
import { getAdUnitIdsForPath } from './utils'

type Props = {
  adUnitPaths: AdUnitPath[]
  product: AdUnitsTableProduct
}

export const useColumns = ({ adUnitPaths, product }: Props): ColumnDef<AdUnitForProductTable>[] => {
  const ability = useAbility()
  const { t } = useTranslation('features/adUnit')
  const { t: tProduct } = useTranslation('features/product')
  const { t: tCommon } = useTranslation('common')

  const { overwritePriceGeniusState, priceGeniusAlwaysOn } = usePriceGeniusAlwaysOn()
  const { control, setValue } = useFormContext<AdUnitsAttributesSchema>()

  const cannotUpdate: boolean = ability.cannot('update', `Product-${product}`)

  return [
    {
      accessorKey: 'name',
      header: t('list.header.adUnitName'),
    },
    {
      accessorKey: 'divId',
      header: t('list.header.divId'),
    },
    {
      accessorKey: 'path',
      header: t('list.header.adUnitPath'),
    },
    {
      accessorKey: `${product}Enabled`,
      cell: ({
        cell: { getValue },
        row: {
          original: { alerts, id, outOfPage, path },
        },
      }) => {
        const adUnitAlerts: Alert<AdUnitAlert>[] = alerts.filter(
          (alert: Alert<AdUnitAlert>): boolean => alert.name.startsWith(product),
        )

        return (
          <FlexBox axis='x'>
            <Switch<AdUnitsAttributesSchema>
              control={control}
              disabled={
                cannotUpdate ||
                (product === 'priceGenius' && overwritePriceGeniusState(product)) ||
                (product === 'viewabilityTools' && outOfPage)
              }
              name={`adUnitsAttributes.${id}`}
              {...((product === 'viewabilityTools' || product === 'priceGenius') && {
                onChange: (e: ChangeEvent<HTMLInputElement>): void => {
                  const checked: boolean = e.target.checked
                  const adUnitIdsWithSamePath: AdUnitForProductTable['id'][] = getAdUnitIdsForPath(
                    adUnitPaths,
                    path,
                  )
                  adUnitIdsWithSamePath.forEach((adUnitId: AdUnitForProductTable['id']): void => {
                    setValue(`adUnitsAttributes.${adUnitId}`, checked, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  })
                },
              })}
              value={getValue()}
              {...(product === 'priceGenius' &&
                overwritePriceGeniusState(product) && {
                  tooltip: {
                    content: t('form.priceGenius.enabled.tooltip'),
                    link: {
                      title: tCommon('learnMore'),
                      url: KNOWLEDGE_BASE_URL,
                    },
                  },
                  tooltipMode: 'wrap',
                  uiValueOverride: priceGeniusAlwaysOn,
                })}
            />

            {adUnitAlerts.length > 0 && <AdUnitAlerts alerts={adUnitAlerts} />}
          </FlexBox>
        )
      },
      header: tProduct(`product.${product}`),
      meta: { nonClickable: true },
    },
    {
      accessorKey: 'updatedAt',
      cell: props => <LastModifiedCell {...props} />,
      header: t('list.header.lastModified'),
    },
    {
      cell: () => <MoreVert data-cy='more-vert-icon' />,
      header: t('list.header.actions'),
      id: 'actions',
      meta: {
        centered: true,
        nonClickable: true,
      },
      size: COLUMN_SIZE.SM,
    },
  ]
}
